import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Input, Radio, Select, Stepper, Textarea } from '@dreamtree-org/twreact-ui';
import { Search, Store, Truck } from 'lucide-react';
import { KeyValue, Money, PageHeader, SectionCard, StatusBadge } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { isValidPin } from '../lib/cart';
import { useStorefront } from '../StorefrontContext';
import SlotPicker, { dayLabel } from '../components/SlotPicker';
import { RepairList } from './MyRepairs';

const ISSUES = [
  { value: 'Display', label: 'Display: cracked, lines, flicker' },
  { value: 'Keyboard', label: 'Keyboard or trackpad' },
  { value: 'Battery', label: 'Battery or charging' },
  { value: 'Performance', label: 'Slow, overheating or fan noise' },
  { value: 'No power', label: 'Does not switch on' },
  { value: 'Liquid damage', label: 'Liquid spill' },
  { value: 'Software', label: 'OS, virus or data recovery' },
  { value: 'Upgrade', label: 'RAM / SSD upgrade' },
  { value: 'Other', label: 'Something else' },
];

const STEPS = [
  { title: 'Device', description: 'Model and serial' },
  { title: 'Issue', description: 'What is wrong' },
  { title: 'Pickup', description: 'Where and when' },
  { title: 'Review', description: 'Fees and consent' },
];

const DIAGNOSTIC_FEE = 29900;
const radio = 'var(--color-primary-600)';

/**
 * WEB-08 repair booking wizard: device, issue, pickup or drop-off with
 * a slot, fee disclosure and explicit data-access consent (C-06).
 */
export default function RepairBooking() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const create = useMutation('sfRepairCreate');
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState(false);
  const [f, setF] = useState({ model: '', serial: '', issue: '', description: '', mode: 'pickup', address: '', pin: sf.pin || '', slot: null, phone: (sf.session && sf.session.phone) || '', consent_data: false, backup_ack: false, fee_ack: false });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const slots = useQuery('sfRepairSlots', { mode: f.mode }, { select: (r) => r.data });

  const stepErrors = [
    { model: !f.model.trim() ? 'Tell us the device model' : '' },
    { issue: !f.issue ? 'Pick the main issue' : '', description: !f.description.trim() ? 'A sentence or two helps the technician' : '' },
    { pin: f.mode === 'pickup' && !isValidPin(f.pin) ? 'Enter a 6-digit PIN code' : '', address: f.mode === 'pickup' && !f.address.trim() ? 'Pickup address is required' : '', slot: !f.slot ? 'Pick a slot' : '' },
    { consent: !f.consent_data ? 'Consent is required' : '', fee: !f.fee_ack ? 'Please acknowledge the fee' : '' },
  ];
  const errs = stepErrors[step];
  const valid = !Object.values(errs).some(Boolean);
  const err = (k) => (touched && errs[k]) || undefined;

  const next = () => {
    setTouched(true);
    if (!valid) return;
    setTouched(false);
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    if (!sf.session) {
      navigate(`/account/login?next=${encodeURIComponent('/repair')}`);
      return;
    }
    try {
      const r = await create.run(f);
      navigate(`/repair/${r.data.id}`);
    } catch {
      /* shown below */
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Book a repair" subtitle="Doorstep pickup across Bengaluru. You approve a written estimate before any work starts." />
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <Card className="space-y-5 p-4 sm:p-6">
            <div className="overflow-x-auto">
              <div className="min-w-[30rem]"><Stepper steps={STEPS} currentStep={step} orientation="horizontal" onStepClick={(_, i) => i < step && setStep(i)} /></div>
            </div>

            {step === 0 ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Device brand and model" required placeholder="e.g. Dell Inspiron 15 3520" value={f.model} onChange={(e) => set('model', e.target.value)} error={err('model')} />
                <Input label="Serial number (optional)" placeholder="On the label or in the settings" value={f.serial} onChange={(e) => set('serial', e.target.value.toUpperCase())} helperText="Helps us check warranty. We never publish it." />
              </div>
            ) : null}

            {step === 1 ? (
              <div className="space-y-3">
                <Select label="Main issue" required options={ISSUES} value={f.issue} onChange={(v) => set('issue', v)} placeholder="Choose the closest match" error={err('issue')} closeOnSelect />
                <Textarea label="Describe the problem" required rows={4} maxLength={800} showCount value={f.description} onChange={(e) => set('description', e.target.value)} error={err('description')} helperText="When did it start? Any drop or spill?" />
                <p className="text-xs opacity-70">Have photos of the damage? Show them to our technician at pickup; they are added to your repair record with the device's condition.</p>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="space-y-4">
                <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Pickup or drop-off">
                  {[
                    ['pickup', Truck, 'Doorstep pickup', 'Free within Bengaluru'],
                    ['dropoff', Store, 'Drop off at store', sf.brand.name],
                  ].map(([v, Icon, t, s]) => (
                    <div key={v} className={`flex items-center gap-3 rounded-lg border p-3 ${f.mode === v ? 'border-primary-600 bg-primary-50' : 'border-[var(--app-border)]'}`}>
                      <Radio name="mode" value={v} checked={f.mode === v} onChange={() => setF((x) => ({ ...x, mode: v, slot: null }))} radioColor={radio} ariaLabel={t} />
                      <Icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                      <span className="text-sm"><span className="block font-semibold">{t}</span><span className="block text-xs opacity-70">{s}</span></span>
                    </div>
                  ))}
                </div>
                {f.mode === 'pickup' ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-2"><Input label="Pickup address" required value={f.address} onChange={(e) => set('address', e.target.value)} error={err('address')} placeholder="Flat, street, locality" /></div>
                    <Input label="PIN code" type="tel" inputMode="numeric" maxLength={6} required value={f.pin} onChange={(e) => set('pin', e.target.value.replace(/\D/g, ''))} error={err('pin')} />
                  </div>
                ) : (
                  <Alert variant="info">Bring the device and its charger to {sf.brand.name}. Show the booking ID at the counter.</Alert>
                )}
                <div>
                  <div className="mb-2 text-sm font-medium">{f.mode === 'pickup' ? 'Pickup slot' : 'Drop-off slot'}</div>
                  <SlotPicker days={slots.data} value={f.slot} onChange={(s) => set('slot', s)} />
                  {err('slot') ? <p className="mt-1 text-sm text-error-600">{err('slot')}</p> : null}
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="space-y-4">
                <KeyValue
                  items={[
                    { label: 'Device', value: `${f.model}${f.serial ? ` (serial ${f.serial})` : ''}` },
                    { label: 'Issue', value: f.issue },
                    { label: f.mode === 'pickup' ? 'Pickup' : 'Drop-off', value: f.slot ? `${dayLabel(f.slot.date)}, ${f.slot.label}` : null },
                    { label: 'Where', value: f.mode === 'pickup' ? `${f.address}, ${f.pin}` : 'At the store' },
                  ]}
                />
                <Alert variant="warning" title="Fees">
                  <ul className="list-disc space-y-1 pl-5">
                    <li>Diagnostic fee <Money paise={DIAGNOSTIC_FEE} />, waived if you approve the repair.</li>
                    <li>Pickup and return within Bengaluru are free.</li>
                    <li>You get a written estimate. Nothing is repaired or charged without your approval.</li>
                  </ul>
                </Alert>
                <div className="space-y-2">
                  <Checkbox checked={f.consent_data} onChange={(e) => set('consent_data', e.target.checked)} required label="I allow the technician to access the device's storage only as needed to diagnose and test the repair. Personal files will not be opened or copied." />
                  {err('consent') ? <p className="text-sm text-error-600">{err('consent')}</p> : null}
                  <Checkbox checked={f.backup_ack} onChange={(e) => set('backup_ack', e.target.checked)} label="I have backed up my data (recommended; optional)." />
                  <Checkbox checked={f.fee_ack} onChange={(e) => set('fee_ack', e.target.checked)} required label="I understand the ₹299 diagnostic fee if I decline the estimate." />
                  {err('fee') ? <p className="text-sm text-error-600">{err('fee')}</p> : null}
                </div>
                {create.error ? <Alert variant="error">{create.error}</Alert> : null}
              </div>
            ) : null}

            <div className="flex flex-wrap justify-between gap-2 border-t border-[var(--app-border)] pt-4">
              <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>Back</Button>
              {step < STEPS.length - 1 ? <Button onClick={next}>Continue</Button> : <Button loading={create.loading} onClick={submit}>{sf.session ? 'Confirm booking' : 'Sign in to book'}</Button>}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          {sf.session ? (
            <>
              <TrackBox />
              <MyRepairs />
            </>
          ) : (
            <Alert variant="info" title="Sign in to book and track repairs">
              We text you a one-time code; your repairs, estimates and approvals stay in your account.
              <div className="mt-2"><Button size="sm" variant="outline" onClick={() => navigate(`/account/login?next=${encodeURIComponent('/repair')}`)}>Sign in</Button></div>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}

function TrackBox() {
  const navigate = useNavigate();
  const [id, setId] = useState('');
  return (
    <SectionCard title="Track a repair">
      <form className="flex items-start gap-2" onSubmit={(e) => { e.preventDefault(); if (id.trim()) navigate(`/repair/${encodeURIComponent(id.trim().toUpperCase())}`); }}>
        <div className="flex-1"><Input
          aria-label="Repair ID"
          placeholder="e.g. ST-2001"
          value={id}
          onChange={(e) => setId(e.target.value)}
          leftIcon={<Search className="h-4 w-4" />} /></div>
        <Button type="submit" variant="outline">Track</Button>
      </form>
    </SectionCard>
  );
}

function MyRepairs() {
  const q = useQuery('sfRepairs', {}, { select: (r) => r.data });
  if (!q.data || !q.data.length) return null;
  return (
    <SectionCard title="Recent repairs" actions={<Link to="/account/repairs" className="text-sm font-medium hover:underline">See all</Link>}>
      <RepairList repairs={q.data} limit={4} />
    </SectionCard>
  );
}
